import {FC, useMemo, useCallback} from "react";
import {Box, BoxProps, LinearProgress} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {
	DataGridPro,
	GridColDef,
	GRID_CHECKBOX_SELECTION_COL_DEF
} from "@mui/x-data-grid-pro";
import {type ListQuizItemDto} from "@/app2/shared/data";
import {useQuizzesListGrid} from "./useQuizzesListGrid";
import {QuizzesListGridToolbar} from "./QuizzesListGridToolbar";

const useStyles = makeStyles()((theme) => ({
	root: {
		marginTop: theme.spacing(2.5)
	},
	searchResults: {
		"& .MuiDataGrid-row": {
			cursor: "pointer"
		},
		"& .MuiDataGrid-columnHeaders": {
			borderBottom: "none"
		},
		"& .MuiDataGrid-cell": {
			borderTop: "none",
			borderBottom: "none"
		},
		"& .MuiDataGrid-footerContainer": {
			borderTop: "none"
		},
		"& th": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		}
	}
}));

export interface AttachQuizInlineProps extends Omit<BoxProps, "onChange"> {
	quizId?: number | null;
	onChange: (quizId: number | null) => void;
}

/**
 * Quiz picker for lesson editor – lists cohort quizzes from improve-api with server-side
 * pagination, filtering, and sorting. Single selection.
 */
export const AttachQuizInline: FC<AttachQuizInlineProps> = ({
	quizId,
	onChange,
	className,
	...props
}) => {
	const {classes, cx} = useStyles();
	const {
		rows,
		rowCount,
		isLoading,
		page,
		pageSize,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		onSortModelChange,
		defaultInitialState
	} = useQuizzesListGrid();

	const columns: GridColDef<ListQuizItemDto>[] = useMemo(
		() => [
			{
				...GRID_CHECKBOX_SELECTION_COL_DEF,
				type: "string",
				hideable: false
			},
			{
				field: "title",
				type: "string",
				flex: 1,
				headerName: "Title",
				minWidth: 240
			},
			{
				field: "languages",
				type: "string",
				filterable: false,
				flex: 1,
				headerName: "Supported Languages",
				valueGetter: (params: { row: ListQuizItemDto }) =>
					Array.isArray(params.row.languages)
						? params.row.languages.join(", ")
						: params.row.languages ?? ""
			}
		],
		[]
	);

	const selectionModel = quizId ? [quizId] : [];

	const Toolbar = useCallback(() => <QuizzesListGridToolbar />, []);

	return (
		<Box className={cx(classes.root, classes.searchResults, className)} {...props}>
			{isLoading && !rows.length && <LinearProgress />}
			<DataGridPro<ListQuizItemDto>
				autoHeight
				disableMultipleSelection
				checkboxSelection
				keepNonExistentRowsSelected
				loading={isLoading}
				rows={rows}
				columns={columns}
				getRowId={(row) => row.id}
				selectionModel={selectionModel}
				onSelectionModelChange={(model) => {
					onChange((model?.pop?.() as number) ?? null);
				}}
				sortingMode="server"
				page={page}
				pageSize={pageSize}
				onPageSizeChange={onPageSizeChange}
				onPageChange={onPageChange}
				paginationMode="server"
				pagination
				rowCount={rowCount}
				onSortModelChange={onSortModelChange}
				filterMode="server"
				onFilterModelChange={onFilterChange}
				components={{Toolbar}}
				rowsPerPageOptions={[25, 50, 100, 200]}
				initialState={defaultInitialState}
				disableSelectionOnClick
			/>
			{isLoading && <LinearProgress />}
		</Box>
	);
};
